import { useEffect, useState } from 'react';
import { apiFetch } from './useApi.js';

/**
 * Direct printing via the print helper on the shipping PC.
 * See server: services/printAgentService.js.
 *
 *   const { canPrint, printerFor } = usePrintStatus();
 *   await sendToPrinter({ docType: 'batch_label', path: `/stock/batches/${id}/label.pdf` });
 */

// One shared status fetch for every Print button on the page (30s cache).
let cache = { at: 0, data: null, pending: null };
const listeners = new Set();

async function loadStatus(force = false) {
  if (!force && cache.data && Date.now() - cache.at < 30000) return cache.data;
  if (cache.pending) return cache.pending;
  cache.pending = apiFetch('/print/status')
    .then((data) => { cache = { at: Date.now(), data, pending: null }; listeners.forEach((l) => l(data)); return data; })
    .catch(() => { cache.pending = null; return cache.data || { agents: [], doc_types: {} }; });
  return cache.pending;
}

export function refreshPrintStatus() { return loadStatus(true); }

/** Current status (cached up to 30s) — for code paths that aren't components. */
export function getPrintStatus() { return loadStatus(); }

export function usePrintStatus() {
  const [data, setData] = useState(cache.data);
  useEffect(() => {
    listeners.add(setData);
    loadStatus().then(setData);
    return () => listeners.delete(setData);
  }, []);
  const docTypes = data?.doc_types || {};
  return {
    loaded: !!data,
    /** A printer is set for this document type (helper may still be offline). */
    isConfigured: (docType) => !!docTypes[docType],
    /** Set up and the shipping PC is connected right now. */
    canPrint: (docType) => !!docTypes[docType]?.online,
    printerFor: (docType) => docTypes[docType]?.printer_name || null,
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Sends a document to its printer and waits (up to ~25s) for the helper to
 * report back. Resolves { printer } when printed; throws with a readable
 * message otherwise.
 */
export async function sendToPrinter({ docType, path, method = 'GET', body, title }) {
  const job = await apiFetch('/print', { method: 'POST', body: { doc_type: docType, path, method, body, title } });
  const deadline = Date.now() + 25000;
  while (Date.now() < deadline) {
    await sleep(1000);
    const j = await apiFetch(`/print/jobs/${job.id}`);
    if (j.status === 'completed') return { printer: j.printer_name };
    if (j.status === 'failed' || j.status === 'expired') {
      throw new Error(`Couldn't print on ${j.printer_name}: ${j.error_message || j.status}`);
    }
  }
  throw new Error(`Sent to ${job.printer_name}, but the shipping PC hasn't confirmed it printed yet — check the printer.`);
}
